import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import {
    CheckCircle,
    XCircle,
    AlertCircle,
    Calendar,
    Clock,
    Info,
    Mail,
    Phone,
    User,
    FileText,
    PartyPopper
} from 'lucide-react';
import Swal from 'sweetalert2';
import { getData } from '../../../../services/apiService'

const PaymentStatus = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [paymentData, setPaymentData] = useState(null);
    const [participantDetails, setParticipantDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('loading');
    const [apiTriggered, setApiTriggered] = useState(false);
    const [isApiLoading, setIsApiLoading] = useState(false);

    // Encryption key - use environment variable in production
    const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'your-secure-key-min-16-chars';

    // Encryption/Decryption helpers
    const encryptData = (data) => {
        try {
            const jsonString = JSON.stringify(data);
            return CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
        } catch (error) {
            console.error('Encryption error:', error);
            return null;
        }
    };

    const decryptData = (encryptedData) => {
        try {
            if (!encryptedData) return null;
            const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
            const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
            if (!decryptedString) return null;
            return JSON.parse(decryptedString);
        } catch (error) {
            console.error('Decryption error:', error);
            return null;
        }
    };

    const clearStoredData = () => {
        sessionStorage.removeItem('pS');
    };

    const BackToHome = () => {
        clearStoredData();
        if (participantDetails?.Suburl) {
            navigate(`/register/${participantDetails.Suburl}`);
        } else {
            navigate('/');
        }
    };

    const triggerPaymentSuccessAPI = async (email, eventId, ParticipantType) => {
        try {
            if (!apiTriggered && email && eventId) {
                setApiTriggered(true);
                setIsApiLoading(true);
                const format = 'pdf';
                const response = await getData(
                    `Report/GetParticipantPayDetails-Mail?email=${email}&eventid=${eventId}&participantid=${ParticipantType}&format=${format}`
                );

                setIsApiLoading(false);
            }
        } catch (error) {
            console.error('Error triggering payment success API:', error);
            setIsApiLoading(false);
        }
    };

    const handleNewPayment = async (urlParams) => {
        const urlData = {};
        urlParams.forEach((value, key) => {
            urlData[key] = value;
        });

        // Generate unique session ID
        const sessionId = `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        // Create participant data
        const participantData = {
            ParticipantType: urlData.Participant_type,
            receiptNo: urlData.Receipt_No,
            eventName: urlData.Event_Name,
            eventId: urlData.Event_Id,
            name: urlData.Name ? decodeURIComponent(urlData.Name) : '',
            email: urlData.email || urlData.EMAIL,
            phone: urlData.phone || urlData.PHONE,
            txndate: urlData.txndate || urlData.TXNDATE,
            Suburl: urlData.Suburl || urlData.SUBURL,
            error: urlData.error ? decodeURIComponent(urlData.error) : null,
            sessionId: sessionId
        };

        // Create payment data
        const paymentData = {
            status: urlData.STATUS || urlData.status,
            amount: urlData.TXNAMOUNT || urlData.amount,
            email: urlData.email || urlData.EMAIL,
            phone: urlData.phone || urlData.PHONE,
            txndate: urlData.txndate || urlData.TXNDATE,
            timestamp: new Date().toISOString(),
            Event_Name: urlData.Event_Name,
            Receipt_No: urlData.Receipt_No,
            orderId: urlData.ORDERID,
            txnId: urlData.TXNID,
            sessionId: sessionId
        };

        // Store encrypted data
        const storageData = {
            payment: paymentData,
            participant: participantData,
            timestamp: Date.now(),
            expires: Date.now() + (60 * 60 * 1000), // 1 hour expiration
            version: '1.0'
        };

        // Encrypt and store in sessionStorage
        const encryptedData = encryptData(storageData);
        if (encryptedData) {
            sessionStorage.setItem('pS', encryptedData);

        }

        // Update state
        setPaymentData(paymentData);
        setParticipantDetails(participantData);

        // Determine status
        const paymentStatus = paymentData.status === 'TXN_SUCCESS' ? 'success' :
            paymentData.status === 'TXN_FAILURE' ? 'failed' :
                paymentData.status === 'PENDING' ? 'pending' : 'failed';
        setStatus(paymentStatus);

        // Clear URL for security
        if (window.history.replaceState) {
            window.history.replaceState(null, '', window.location.pathname);
        }

        // Trigger success API if needed
        if (paymentStatus === 'success') {
            const emailToUse = paymentData.email || participantData.email;
            const eventId = participantData.eventId;
            const ParticipantType = participantData.ParticipantType
            if (emailToUse && eventId && ParticipantType) {
                await triggerPaymentSuccessAPI(emailToUse, eventId, ParticipantType);
            }
        }
    };

    const handleStoredPayment = async () => {
        // Try sessionStorage first (primary storage)
        const encryptedSession = sessionStorage.getItem('pS');

        if (encryptedSession) {
            const storedData = decryptData(encryptedSession);

            if (storedData && storedData.expires > Date.now()) {
                // Valid sessionStorage data
                setPaymentData(storedData.payment);
                setParticipantDetails(storedData.participant);

                const paymentStatus = storedData.payment.status === 'TXN_SUCCESS' ? 'success' :
                    storedData.payment.status === 'TXN_FAILURE' ? 'failed' :
                        storedData.payment.status === 'PENDING' ? 'pending' : 'failed';
                setStatus(paymentStatus);
                return;
            } else {
                // Data expired or invalid
                clearStoredData();
            }
        }

        // No valid data found
        setStatus('failed');
    };

    const processPaymentData = async () => {
        try {
            setLoading(true);
            const urlParams = new URLSearchParams(location.search);

            // Check if we have URL params (fresh payment redirect)
            const hasStatus = urlParams.get('STATUS');

            if (urlParams.toString() && hasStatus) {
                await handleNewPayment(urlParams);
            } else {
                // Check for stored data on refresh
                await handleStoredPayment();
            }

        } catch (error) {
            console.error('Error processing payment:', error);
            setStatus('failed');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        processPaymentData();

        // Cleanup on unmount (optional)
        return () => {
            // Keep data for refresh, clear only on navigation
            // clearStoredData();
        };
    }, [location]);

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        const num = parseFloat(amount);
        return `₹${num.toLocaleString('en-IN')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            if (dateString === 'Date') {
                return new Date().toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }

            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const getStatusConfig = () => {
        const configs = {
            success: {
                icon: <CheckCircle className="w-12 h-12" />,
                title: 'Payment Successful',
                message: 'Your payment has been processed successfully.',
                headerBg: 'bg-gradient-to-r from-emerald-500 to-green-600',
                bodyBg: 'bg-gradient-to-b from-emerald-50 via-green-50 to-emerald-25',
                iconColor: 'text-emerald-600',
                textColor: 'text-emerald-700',
                borderColor: 'border-emerald-200',
                buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
                lightColor: 'bg-emerald-50',
                mediumColor: 'bg-emerald-100',
                darkColor: 'bg-emerald-600',
                greetingIcon: <PartyPopper className="w-8 h-8" />
            },
            failed: {
                icon: <XCircle className="w-12 h-12" />,
                title: 'Payment Failed',
                message: 'Transaction could not be completed.',
                headerBg: 'bg-gradient-to-r from-rose-500 to-red-600',
                bodyBg: 'bg-gradient-to-b from-rose-50 via-red-50 to-rose-25',
                iconColor: 'text-rose-600',
                textColor: 'text-rose-700',
                borderColor: 'border-rose-200',
                buttonColor: 'bg-rose-600 hover:bg-rose-700',
                lightColor: 'bg-rose-50',
                mediumColor: 'bg-rose-100',
                darkColor: 'bg-rose-600'
            },
            pending: {
                icon: <Clock className="w-12 h-12" />,
                title: 'Payment Pending',
                message: 'Your payment is being processed.',
                headerBg: 'bg-gradient-to-r from-amber-500 to-orange-600',
                bodyBg: 'bg-gradient-to-b from-amber-50 via-orange-50 to-amber-25',
                iconColor: 'text-amber-600',
                textColor: 'text-amber-700',
                borderColor: 'border-amber-200',
                buttonColor: 'bg-amber-600 hover:bg-amber-700',
                lightColor: 'bg-amber-50',
                mediumColor: 'bg-amber-100',
                darkColor: 'bg-amber-600'
            },
            default: {
                icon: <AlertCircle className="w-12 h-12" />,
                title: 'Payment Status',
                message: 'Processing payment status...',
                headerBg: 'bg-gradient-to-r from-gray-500 to-gray-600',
                bodyBg: 'bg-gradient-to-b from-gray-50 via-gray-50 to-gray-25',
                iconColor: 'text-gray-600',
                textColor: 'text-gray-700',
                borderColor: 'border-gray-200',
                buttonColor: 'bg-gray-600 hover:bg-gray-700',
                lightColor: 'bg-gray-50',
                mediumColor: 'bg-gray-100',
                darkColor: 'bg-gray-600'
            }
        };

        return configs[status] || configs.default;
    };

    const config = getStatusConfig();

    const handleDownloadReceipt = async () => {
        try {

            const email = participantDetails?.email || paymentData?.email;
            const eventId = participantDetails?.eventId;
            const ParticipantType = participantDetails?.ParticipantType

            if (!email || !eventId || !ParticipantType) {
                Swal.fire({
                    title: 'Error',
                    text: 'Email or Event ID not found for downloading receipt',
                    icon: 'error',
                    confirmButtonColor: config.darkColor
                });
                return;
            }

            setIsApiLoading(true);

            const format = 'pdf';
            const response = await getData(
                `Report/DownloadReceipt?email=${email}&eventid=${eventId}&ParticipantID=${ParticipantType}&format=${format}`,
                'blob'
            );

            setIsApiLoading(false);

            if (response instanceof Blob || (response.data && response.data instanceof Blob)) {
                const blob = response.data || response;

                if (blob.size === 0) {
                    throw new Error('Empty PDF received');
                }

                if (!blob.type.includes('pdf') && !blob.type.includes('application/pdf')) {
                    const blobWithType = new Blob([blob], { type: 'application/pdf' });
                    downloadBlob(blobWithType);
                } else {
                    downloadBlob(blob);
                }
            } else if (response && typeof response === 'string') {
                if (response.startsWith('%PDF')) {
                    const byteCharacters = atob(response);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/pdf' });
                    downloadBlob(blob);
                } else if (response.startsWith('JVBER')) {
                    try {
                        const byteCharacters = atob(response);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: 'application/pdf' });
                        downloadBlob(blob);
                    } catch (error) {
                        const blob = new Blob([response], { type: 'application/pdf' });
                        downloadBlob(blob);
                    }
                } else {
                    try {
                        const jsonResponse = JSON.parse(response);
                        if (jsonResponse.error) {
                            throw new Error(jsonResponse.error || 'Error downloading receipt');
                        }
                    } catch (jsonError) {
                        const blob = new Blob([response], { type: 'application/pdf' });
                        downloadBlob(blob);
                    }
                }
            } else {
                Swal.fire({
                    title: 'Error',
                    text: 'Invalid receipt data received',
                    icon: 'error',
                    confirmButtonColor: config.darkColor
                });
            }
        } catch (error) {
            console.error('Error downloading receipt:', error);
            setIsApiLoading(false);

            Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to download receipt. Please try again.',
                icon: 'error',
                confirmButtonColor: config.darkColor
            });
        }
    };

    const downloadBlob = (blob) => {
        try {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Receipt_${participantDetails?.receiptNo || paymentData?.Receipt_No || 'receipt'}.pdf`;
            a.style.display = 'none';

            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 100);

            Swal.fire({
                title: 'Success!',
                text: 'Receipt downloaded successfully',
                icon: 'success',
                confirmButtonColor: config.darkColor
            });
        } catch (error) {
            console.error('Error in downloadBlob:', error);
            throw error;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className={`animate-spin rounded-full h-16 w-16 border-4 ${config.borderColor} border-t-transparent mx-auto mb-4`}></div>
                    <p className="text-gray-600 font-medium">Processing payment...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${config.bodyBg} py-8 px-4 transition-colors duration-300`}>
            <div className="max-w-4xl mx-auto border-2 border-gray-200 rounded-xl bg-white">
                {/* Header Status Card */}
                <div className={`shadow-xl ${config.headerBg} text-white p-8 transform transition-all duration-300 hover:shadow-2xl`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div className="flex items-center space-x-6 mb-6 md:mb-0">
                            <div className={`bg-white/20 p-4 rounded-full backdrop-blur-sm`}>
                                {React.cloneElement(config.icon, { className: "w-16 h-16 text-white" })}
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold mb-2">{config.title}</h1>
                                <p className="text-white/90 text-lg">{config.message}</p>
                                {status === 'success' && (
                                    <div className="flex items-center mt-3">
                                        <Mail className="w-5 h-5 mr-2" />
                                        <span className="text-sm">
                                            {isApiLoading ? 'Sending receipt...' : 'Receipt sent to your email'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-4xl md:text-5xl font-bold mb-2">{formatCurrency(paymentData?.amount)}</div>
                            <p className="text-white/80">
                                {formatDate(paymentData?.txndate || paymentData?.timestamp || Date.now())}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Two Column Details */}
                <div className="grid lg:grid-cols-2 gap-1 mb-1">
                    {/* Left Column: Transaction Details */}
                    <div className={`bg-white shadow-lg border ${config.borderColor} p-6 transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-center justify-between mb-8">
                            <h3 className={`text-base font-bold ${config.textColor} flex items-center`}>
                                <Calendar className={`w-6 h-6 mr-3 ${config.iconColor}`} />
                                Event Details and Transaction Details
                            </h3>
                            <div className={`px-3 py-1 ${config.lightColor} rounded-full`}>
                                <span className={`text-sm font-medium ${config.textColor}`}>
                                    {status.toUpperCase()}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-8">
                            {/* Event Name */}
                            {participantDetails?.eventName && (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                        <span className="text-gray-600 font-medium flex items-center">
                                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                            Event Name
                                        </span>
                                    </div>
                                    <div className="pl-5">
                                        <span className="font-semibold text-gray-800 text-lg">
                                            {participantDetails.eventName}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Receipt No */}
                            {paymentData?.Receipt_No || participantDetails?.receiptNo ? (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                        <span className="text-gray-600 font-medium flex items-center">
                                            <FileText className="w-4 h-4 mr-2 text-gray-400" />
                                            Receipt No
                                        </span>
                                    </div>
                                    <div className="pl-5">
                                        <span className={`font-mono font-semibold text-lg ${config.textColor} ${config.lightColor} px-4 py-3 rounded-lg block`}>
                                            {paymentData?.Receipt_No || participantDetails?.receiptNo}
                                        </span>
                                    </div>
                                </div>
                            ) : null}

                            {/* Transaction Date */}
                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Clock className="w-4 h-4 mr-2 text-gray-400" />
                                        Transaction Date & Time
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {formatDate(paymentData?.txndate || paymentData?.timestamp)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Participant Details */}
                    <div className={`bg-white shadow-lg border ${config.borderColor} p-6 transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-center justify-between mb-8">
                            <h3 className={`text-base font-bold ${config.textColor} flex items-center`}>
                                <User className={`w-6 h-6 mr-3 ${config.iconColor}`} />
                                Participant Details
                            </h3>
                            {status === 'success' && (
                                <div className={`px-3 py-1 ${config.lightColor} rounded-full`}>
                                    <span className={`text-sm font-medium ${config.textColor}`}>
                                        Registered
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="space-y-8">
                            {/* Participant Name */}
                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium">Participant Name</span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-semibold text-gray-800 text-xl">
                                        {participantDetails?.name || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                        Email
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg break-all">
                                        {participantDetails?.email || paymentData?.email || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            {/* Mobile */}
                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                        Mobile
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {participantDetails?.phone || paymentData?.phone || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Greeting Message for Success OR Important Information for other statuses */}
                {status === 'success' ? (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor} transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex flex-col items-center text-center">
                            {/* Celebration Icon */}
                            <div className={`mb-6 p-4 rounded-full ${config.mediumColor}`}>
                                {React.cloneElement(config.greetingIcon, { className: `w-12 h-12 ${config.iconColor}` })}
                            </div>

                            {/* Main Greeting */}
                            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${config.textColor}`}>
                                Registration Completed Successfully!!!
                            </h2>

                            {/* Sub Message */}
                            <div className={`max-w-2xl mx-auto mb-8 p-4 ${config.mediumColor} rounded-lg`}>
                                <p className="text-lg font-medium text-gray-700">
                                    You will receive the acknowledgement to your registered mail ID.
                                </p>
                                <p className={`text-xl font-bold mt-2 ${config.textColor}`}>
                                    Thank You.
                                </p>
                            </div>

                            {/* Additional Info */}
                            <div className="grid md:grid-cols-2 gap-6 w-full max-w-2xl">
                                <div className="space-y-3">
                                    <div className="flex items-start">
                                        <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                        <p className="text-gray-700">Receipt No: <span className="font-bold">{paymentData?.Receipt_No || participantDetails?.receiptNo}</span></p>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-start">
                                        <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                        <p className="text-gray-700">Check spam folder if email not received</p>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={handleDownloadReceipt}
                                disabled={isApiLoading}
                                className="flex items-center gap-2 px-6 py-3 mt-10 rounded-xl 
                                bg-gradient-to-r from-emerald-500 to-teal-600 
                                text-white font-semibold shadow-lg 
                                hover:from-emerald-600 hover:to-teal-700 
                                hover:shadow-xl active:scale-95 
                                disabled:opacity-50 disabled:cursor-not-allowed
                                transition-all duration-200"
                            >
                                {isApiLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                                        </svg>
                                        Download Receipt
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor} transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-start">
                            <div className={`p-3 rounded-full ${config.mediumColor} mr-4 flex-shrink-0`}>
                                <Info className={`w-6 h-6 ${config.iconColor}`} />
                            </div>
                            <div>
                                <h4 className={`text-xl font-bold mb-4 ${config.textColor}`}>
                                    📋 Important Information
                                </h4>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">You can retry the payment from your dashboard</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">Contact support if amount was deducted but failed</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-center mt-10">
                            <button
                                onClick={BackToHome}
                                className="px-6 py-3 text-sm md:text-base font-semibold
                                        text-indigo-600
                                        bg-transparent
                                        border-2 border-indigo-600
                                        rounded-xl
                                        hover:bg-indigo-600 hover:text-white
                                        hover:-translate-y-0.5
                                        hover:shadow-lg
                                        transition-all duration-300 ease-in-out
                                        focus:outline-none focus:ring-4 focus:ring-indigo-300"
                            >
                                ← Back to Registration
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentStatus;